/**
 * Extra Slide Component
 */
if (!customElements.get('subscription-extra-slide')) {
  customElements.define(
    'subscription-extra-slide',
    class SubscriptionExtraSlide extends HTMLElement {
      constructor() {
        super();
        this.unsubscribe = [];
        this.selectedExtras = {};

        this.handleProductSelection = this.handleProductSelection.bind(this);
        this.handleAddButtonClick = this.handleAddButtonClick.bind(this);
        this.handleSkip = this.handleSkip.bind(this);
        this.handleBeforeCheckout = this.handleBeforeCheckout.bind(this);
        this.handleFilterButtonClick = this.handleFilterButtonClick.bind(this);
      }

      connectedCallback() {
        Utils.HeightManager.init(this, '--extra-slide-content-height');
        Utils.setupCommonSlideEventListeners(this, this.handleSlideUpdate.bind(this));

        this.cacheElements();
        this.setupEventListeners();
        this.initializeSelectedProducts();
        this.setInitialFilterAndContentState();
      }

      cacheElements() {
        this.form = this.querySelector('[data-form]');
        this.productCards = this.querySelectorAll('[data-extra-product]');
        this.skipButton = this.querySelector('[data-skip-extra-btn]');
        this.checkboxInputs = this.querySelectorAll('[data-input]');
        this.productImages = this.querySelectorAll('[data-extra-img]');
        this.addButtons = this.querySelectorAll('[data-add-btn]');
        this.quantityWrappers = this.querySelectorAll('[data-quantity-wrapper]');
        this.minusButtons = this.querySelectorAll('[data-minus-btn]');
        this.plusButtons = this.querySelectorAll('[data-plus-btn]');
        this.filterButtons = this.querySelectorAll('[data-filter-btn]');
        this.headings = this.querySelectorAll('[data-heading]');
        this.subheadings = this.querySelectorAll('[data-subheading]');
      }

      setInitialFilterAndContentState() {
        const defaultFilterButton = Array.from(this.filterButtons).find((btn) =>
          btn.classList.contains('tw-is-active')
        );
        let activeIndex = 0;

        if (defaultFilterButton && defaultFilterButton.dataset.filterIndex) {
          activeIndex = parseInt(defaultFilterButton.dataset.filterIndex);
        } else if (this.filterButtons.length > 0) {
          const firstButton = this.filterButtons[0];
          if (firstButton.dataset.filterIndex) {
            activeIndex = parseInt(firstButton.dataset.filterIndex);
          }
          // Ensure the first button is marked active if no other button was
          this.filterButtons.forEach((btn, idx) => btn.classList.toggle('tw-is-active', idx === activeIndex));
        }
        this.updateHeadingsAndSubheadings(activeIndex);
      }

      updateHeadingsAndSubheadings(activeIndex) {
        this.headings.forEach((h) => {
          h.classList.toggle('tw-hidden', parseInt(h.dataset.heading) !== activeIndex);
        });
        this.subheadings.forEach((s) => {
          s.classList.toggle('tw-hidden', parseInt(s.dataset.subheading) !== activeIndex);
        });
      }

      initializeSelectedProducts() {
        this.selectedExtras = {};
        this.processInitialSelection();

        if (Object.keys(this.selectedExtras).length === 0) {
          this.resetProductImages();
        }

        this.publishExtraSelections();
      }

      processInitialSelection() {
        this.checkboxInputs.forEach((input) => {
          if (!input.checked) return;

          const productCard = input.closest('[data-extra-product]');
          if (!productCard?.dataset.id) return;

          const productId = productCard.dataset.id;
          const variantId = input.name;

          this.selectedExtras[productId] = {
            variantId: variantId,
            productId: productId,
            quantity: 1,
          };

          this.updateProductImageState(variantId, true);
        });
      }

      setupEventListeners() {
        this.setupSkipButtonListener();
        this.setupCheckoutListener();
        this.setupAddButtonListeners();
        this.setupFilterButtonListeners();
        this.setupMinusButtonListeners();
        this.setupPlusButtonListeners();
        this.setupExtraProductListeners();
      }

      setupFilterButtonListeners() {
        this.filterButtons.forEach((button) => {
          button.addEventListener('click', this.handleFilterButtonClick);
        });
      }

      setupExtraProductListeners() {
        this.productCards.forEach((card) => {
          card.addEventListener('click', this.handleProductClick.bind(this));
        });
      }

      // setupInputListeners() {
      //   this.checkboxInputs.forEach((input) => {
      //     input.addEventListener('change', this.handleProductSelection);
      //   });
      // }

      setupSkipButtonListener() {
        if (this.skipButton) {
          this.skipButton.addEventListener('click', this.handleSkip);
        }
      }

      setupCheckoutListener() {
        this.unsubscribe.push(subscribe('subscription-flow:before-checkout', this.handleBeforeCheckout));
      }

      setupAddButtonListeners() {
        this.addButtons.forEach((button) => {
          button.addEventListener('click', this.handleAddButtonClick.bind(this));
        });
      }

      setupMinusButtonListeners() {
        this.minusButtons.forEach((button) => {
          button.addEventListener('click', this.handleMinusButtonClick.bind(this));
        });
      }

      setupPlusButtonListeners() {
        this.plusButtons.forEach((button) => {
          button.addEventListener('click', this.handlePlusButtonClick.bind(this));
        });
      }

      handleProductClick(e) {
        const target = e.target;
        const productCard = target.closest('[data-extra-product]');
        const input = productCard?.querySelector('[data-input]');
        const variantId = input.name;

        productCard.classList.add('tw-is-selected');
        this.updateProductImageState(variantId, true);
        this.publishExtraSelections();
      }

      handleFilterButtonClick(e) {
        const button = e.currentTarget;
        const category = button.dataset.category;
        const filterIndex = parseInt(button.dataset.filterIndex);

        this.filterButtons.forEach((btn) => {
          btn.classList.remove('tw-is-active');
        });
        button.classList.add('tw-is-active');

        this.productCards.forEach((card) => {
          const cardCategory = card.dataset.category;
          if (cardCategory === category) {
            card.classList.remove('tw-hidden');
            card.classList.add('tw-block');
          } else {
            card.classList.add('tw-hidden');
            card.classList.remove('tw-block');
          }
        });

        if (!isNaN(filterIndex)) {
          this.updateHeadingsAndSubheadings(filterIndex);
        }
      }

      handleProductSelection(e) {
        const target = e.target;
        const productCard = target.closest('[data-extra-product]');
        const input = productCard?.querySelector('[data-input]');
        if (!productCard?.dataset.id) return;

        const productId = productCard.dataset.id;
        const variantId = input.name;

        if (input.checked) {
          this.addSelectedProduct(productId, variantId, productCard);
        } else {
          this.removeSelectedProduct(productId, variantId, productCard);
        }

        this.publishExtraSelections();
      }

      addSelectedProduct(productId, variantId, productCard) {
        const currentQuantity = this.selectedExtras[productId]?.quantity || 0;

        this.selectedExtras[productId] = {
          variantId: variantId,
          productId: productId,
          quantity: currentQuantity + 1,
        };

        productCard.classList.add('tw-is-selected');
        this.updateProductImageState(variantId, true);
        this.publishExtraSelections();
      }

      removeSelectedProduct(productId, variantId, productCard) {
        const currentQuantity = this.selectedExtras[productId]?.quantity;

        if (currentQuantity > 1) {
          this.selectedExtras[productId].quantity = currentQuantity - 1;
        } else {
          delete this.selectedExtras[productId];
          productCard.classList.remove('tw-is-selected');
          this.updateProductImageState(variantId, false);
        }
        this.publishExtraSelections();
      }

      handleAddButtonClick(e) {
        e.preventDefault();
        e.stopPropagation();
        const button = e.currentTarget;
        const card = button.closest('[data-extra-product]');
        const productId = card.dataset.id;
        const checkbox = card?.querySelector('[data-input]');
        const variantId = checkbox.name;
        const quantityWrapper = card?.querySelector('[data-quantity-wrapper]');
        const quantityInput = quantityWrapper?.querySelector('[data-quantity-input]');

        quantityInput.value = '1';

        if (quantityWrapper) {
          checkbox.checked = true;
          quantityWrapper.classList.remove('!tw-hidden');
          quantityWrapper.classList.add('tw-flex');
          this.addSelectedProduct(productId, variantId, card);
        }
      }

      handleMinusButtonClick(e) {
        e.stopPropagation();
        e.preventDefault();
        const button = e.currentTarget;
        const card = button.closest('[data-extra-product]');
        const productId = card.dataset.id;
        const checkbox = card?.querySelector('[data-input]');
        const variantId = checkbox.name;
        const quantityWrapper = button.closest('[data-quantity-wrapper]');
        const quantityInput = quantityWrapper?.querySelector('[data-quantity-input]');

        if (quantityInput) {
          const newValue = parseInt(quantityInput.value) - 1;
          quantityInput.value = newValue;
          this.removeSelectedProduct(productId, variantId, card);

          if (newValue === 0) {
            checkbox.checked = false;
            quantityWrapper.classList.add('!tw-hidden');
            quantityWrapper.classList.remove('tw-flex');
          }
        }
      }

      handlePlusButtonClick(e) {
        e.stopPropagation();
        e.preventDefault();
        const button = e.currentTarget;
        const card = button.closest('[data-extra-product]');
        const productId = card.dataset.id;
        const checkbox = card?.querySelector('[data-input]');
        const variantId = checkbox.name;
        const quantityWrapper = button.closest('[data-quantity-wrapper]');
        const quantityInput = quantityWrapper?.querySelector('[data-quantity-input]');

        if (quantityInput) {
          quantityInput.value = parseInt(quantityInput.value) + 1;
          this.addSelectedProduct(productId, variantId, card);
        }
      }

      handleSkip(e) {
        e.preventDefault();
        const subscriptionFlow = this.closest('subscription-flow');

        if (this.canProceedToCheckout(subscriptionFlow)) {
          const checkoutButton = subscriptionFlow.querySelector('step-toggle[data-checkout-btn]');
          checkoutButton.click();
        } else {
          this.proceedToNextStep();
        }
      }

      canProceedToCheckout(subscriptionFlow) {
        if (!subscriptionFlow) return false;

        const checkoutButton = subscriptionFlow.querySelector('step-toggle[data-checkout-btn]');
        return (
          checkoutButton && !checkoutButton.hasAttribute('disabled') && !checkoutButton.classList.contains('disabled')
        );
      }

      proceedToNextStep() {
        const currentStep = parseInt(this.dataset.step);
        publish('subscription-flow:change-step', {
          step: currentStep + 1,
          isCheckoutBtn: false,
        });
      }

      handleBeforeCheckout(data) {
        if (!data.items || !Array.isArray(data.items)) return;

        const extraItems = this.prepareExtraItems(data.uniqueBoxId);
        if (extraItems.length > 0) {
          data.items.push(...extraItems);
        }
      }

      updateProductImageState(variantId, isActive = true) {
        if (!this.productImages?.length) return;

        if (isActive) {
          this.productImages.forEach((img) => img.classList.remove('tw-is-active'));
        }

        this.productImages.forEach((image) => {
          if (image.dataset.id === variantId) {
            image.classList.toggle('tw-is-active', isActive);
          }
        });

        const anyActive = Array.from(this.productImages).some((img) => img.classList.contains('tw-is-active'));

        if (!anyActive) {
          this.resetProductImages();
        }
      }

      resetProductImages() {
        if (!this.productImages?.length) return;

        this.productImages.forEach((img) => {
          img.classList.remove('tw-is-active', 'is-active');
        });

        const defaultImage = Array.from(this.productImages).find((img) => img.hasAttribute('data-default-extra-img'));

        if (defaultImage) {
          defaultImage.classList.add('tw-is-active');
        } else if (this.productImages.length > 0) {
          this.productImages[0].classList.add('tw-is-active');
        }
      }

      prepareExtraItems(uniqueBoxId) {
        return Object.values(this.selectedExtras).map((product) => ({
          id: product.variantId,
          quantity: product.quantity,
          properties: {
            _unique_box_id: uniqueBoxId,
          },
        }));
      }

      publishExtraSelections() {
        publish('subscription-flow:extra-selection', {
          extras: this.selectedExtras,
          count: Object.keys(this.selectedExtras).length,
        });
      }

      handleSlideUpdate(data) {
        const isActive = parseInt(this.dataset.step) === data.step;
        if (!isActive) return;

        this.refreshSelectionUI();
        this.updateActiveProductImage();
        // Ensure correct heading/subheading is shown when slide becomes active
        const activeFilterButton = Array.from(this.filterButtons).find((btn) => btn.classList.contains('tw-is-active'));
        if (activeFilterButton && activeFilterButton.dataset.filterIndex) {
          this.updateHeadingsAndSubheadings(parseInt(activeFilterButton.dataset.filterIndex));
        }
      }

      updateActiveProductImage() {
        const selectedProducts = Object.keys(this.selectedExtras);

        if (selectedProducts.length > 0) {
          const firstProductId = selectedProducts[0];
          const firstVariantId = this.selectedExtras[firstProductId].variantId;
          this.updateProductImageState(firstVariantId, true);
        } else {
          this.resetProductImages();
        }
      }

      refreshSelectionUI() {
        this.productCards.forEach((card) => {
          const productId = card.dataset.id;
          const isSelected = !!this.selectedExtras[productId];

          card.classList.toggle('tw-is-selected', isSelected);

          const checkbox = card.querySelector('[data-input]');
          if (checkbox && checkbox.checked !== isSelected) {
            checkbox.checked = isSelected;
          }
        });
      }

      disconnectedCallback() {
        Utils.HeightManager.disconnect(this);
        this.removeEventListeners();
        this.unsubscribeFromEvents();
      }

      removeEventListeners() {
        if (this.skipButton) {
          this.skipButton.removeEventListener('click', this.handleSkip);
        }

        this.checkboxInputs.forEach((input) => {
          input.removeEventListener('change', this.handleProductSelection);
        });

        this.addButtons.forEach((button) => {
          button.removeEventListener('click', this.handleAddButtonClick);
        });

        this.filterButtons.forEach((button) => {
          button.removeEventListener('click', this.handleFilterButtonClick);
        });
      }

      unsubscribeFromEvents() {
        this.unsubscribe.forEach((unsub) => {
          if (typeof unsub === 'function') unsub();
        });
      }
    }
  );
}
